import { TQuote } from '../types';

export type TTickSource = 'live' | 'history';

export interface TTickEvent {
    epoch: number; // milliseconds epoch from quote.DT
    price: number; // close price or tick quote
    symbol: string;
    granularity: number; // ms granularity (0 for pure tick)
    source: TTickSource;
    ohlc?: {
        open?: number;
        high?: number;
        low?: number;
        close?: number;
    };
    raw: TQuote; // original quote object
}

const MAX_EVENTS = 500_000;
const TRIM_CHUNK = 50_000;

class EventLog {
    private _events: TTickEvent[] = [];
    private _epochIndex: Map<number, number> = new Map();

    addHistorical(quotes: TQuote[]) {
        for (const q of quotes) this._append(q, 'history');
        this._enforceBounds();
    }
    addLive(quote: TQuote) {
        this._append(quote, 'live');
        this._enforceBounds();
    }
    private _append(quote: TQuote, source: TTickSource) {
        const epoch = quote.DT ? quote.DT.getTime() : Date.parse(`${quote.Date}Z`);
        const price = typeof quote.Close === 'number' ? quote.Close : (quote.prevClose ?? 0);
        const event: TTickEvent = {
            epoch,
            price,
            symbol: '',
            granularity: 0,
            source,
            ohlc: quote.ohlc ? { open: quote.Open, high: quote.High, low: quote.Low, close: quote.Close } : undefined,
            raw: quote,
        };
        this._events.push(event);
        this._epochIndex.set(epoch, this._events.length - 1);
    }
    private _enforceBounds() {
        if (this._events.length > MAX_EVENTS) {
            const removeCount = Math.min(TRIM_CHUNK, this._events.length - MAX_EVENTS);
            this._events.splice(0, removeCount);
            this._epochIndex.clear();
            for (let i = 0; i < this._events.length; i++) this._epochIndex.set(this._events[i].epoch, i);
        }
    }
    getRange(startEpoch: number, endEpoch: number): TTickEvent[] {
        if (!this._events.length) return [];
        const startIdx = this._binarySearchStart(startEpoch);
        if (startIdx === -1) return [];
        const endIdx = this._binarySearchEnd(endEpoch);
        if (endIdx === -1) return [];
        return this._events.slice(startIdx, endIdx + 1);
    }
    private _binarySearchStart(targetEpoch: number): number {
        let left = 0, right = this._events.length - 1, result = -1;
        while (left <= right) {
            const mid = (left + right) >> 1;
            if (this._events[mid].epoch >= targetEpoch) { result = mid; right = mid - 1; } else left = mid + 1;
        }
        return result;
    }
    private _binarySearchEnd(targetEpoch: number): number {
        let left = 0, right = this._events.length - 1, result = -1;
        while (left <= right) {
            const mid = (left + right) >> 1;
            if (this._events[mid].epoch <= targetEpoch) { result = mid; left = mid + 1; } else right = mid - 1;
        }
        return result;
    }
    latest(): TTickEvent | undefined { return this._events[this._events.length - 1]; }
    size() { return this._events.length; }
    getAll(): TTickEvent[] { return [...this._events]; }
}

export const eventLog = new EventLog();
